/**
 * Inc5 · Exportación de hojas de resultados a CSV, Excel y PDF.
 *
 * Se apoya en `exceljs` y `jspdf`/`jspdf-autotable`, ya presentes como
 * dependencias. La exportación es ÍNTEGRAMENTE de cliente: no hay endpoint que
 * la genere, así que el navegador construye el archivo con los datos que ya
 * tiene en memoria.
 *
 * Consecuencia práctica: solo se exporta lo cargado en la vista. Si el listado
 * está filtrado o paginado, el archivo refleja ese subconjunto, no el total.
 */

// Inc5 · Utilidades de exportación (CSV / Excel / PDF) para las hojas de resultados.
// Reutiliza exceljs y jspdf/jspdf-autotable (ya en dependencias).

import ExcelJS from 'exceljs'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

/**
 * Valor de una celda exportable.
 *
 * Admite `null`/`undefined` a propósito: los exportadores los escriben como celda
 * vacía, de modo que un dato ausente no obliga al llamador a normalizar antes.
 */
export type Celda = string | number | null | undefined

/**
 * Tabla lista para exportar, independiente del formato de salida.
 *
 * La misma estructura alimenta CSV, Excel y PDF; el formato se elige en la
 * función de exportación, no aquí.
 */
export interface TablaExport {
  /** Nombre base del archivo (sin extensión). */
  nombre: string
  /** Título visible (PDF / hoja Excel). */
  titulo: string
  headers: string[]
  rows: Celda[][]
}

function texto(v: Celda): string {
  return v === null || v === undefined ? '' : String(v)
}

function descargarBlob(blob: Blob, nombreArchivo: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = nombreArchivo
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/** CSV con separador ';' y BOM UTF-8 (abre limpio en Excel en español). */
export function exportarCSV(tabla: TablaExport): void {
  const escapar = (v: Celda) => {
    const s = texto(v)
    return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lineas = [tabla.headers, ...tabla.rows].map(fila => fila.map(escapar).join(';'))
  const contenido = '﻿' + lineas.join('\r\n')
  descargarBlob(new Blob([contenido], { type: 'text/csv;charset=utf-8;' }), `${tabla.nombre}.csv`)
}

function rgbToArgb([r, g, b]: [number, number, number]): string {
  return `FF${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase()
}

const THIN_BORDER = {
  top:    { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
  bottom: { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
  left:   { style: 'thin' as const, color: { argb: 'FFD0D0D0' } },
  right:  { style: 'thin' as const, color: { argb: 'FFD0D0D0' } }
}

/** Excel (.xlsx) con encabezado azul, colores por fila, bordes, fusión de celdas y ancho automático. */
export async function exportarExcel(
  tabla: TablaExport,
  opciones?: {
    colorFila?: (i: number) => [number, number, number] | null
    boldFila?: (i: number) => boolean
    merges?: { startRow: number; endRow: number; startCol: number; endCol: number }[]
  }
): Promise<void> {
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet(tabla.titulo.slice(0, 31) || 'Resultados')

  ws.views = [{ state: 'frozen', ySplit: 1 }]

  ws.addRow(tabla.headers)
  const headerRow = ws.getRow(1)
  headerRow.height = 30
  for (let c = 1; c <= tabla.headers.length; c++) {
    const cell = headerRow.getCell(c)
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0E69A0' } }
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 }
    cell.border = THIN_BORDER
  }

  tabla.rows.forEach((fila, rowIdx) => {
    const row = ws.addRow(fila.map(v => (v === null || v === undefined ? '' : v)))
    row.height = 18
    const rgb = opciones?.colorFila?.(rowIdx)
    const isBold = opciones?.boldFila?.(rowIdx) ?? false
    for (let c = 1; c <= tabla.headers.length; c++) {
      const cell = row.getCell(c)
      cell.alignment = { vertical: 'middle', wrapText: true }
      if (rgb) {
        const esUltimaCol = c === tabla.headers.length
        if (isBold || esUltimaCol) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rgbToArgb(rgb) } }
        }
      }
      if (isBold) cell.font = { bold: true, size: 9 }
      cell.border = THIN_BORDER
    }
  })

  if (opciones?.merges) {
    for (const m of opciones.merges) {
      // +2: +1 por base 1, +1 por la fila de encabezado
      ws.mergeCells(m.startRow + 2, m.startCol + 1, m.endRow + 2, m.endCol + 1)
    }
  }

  tabla.headers.forEach((h, i) => {
    const maxCelda = Math.max(h.length, ...tabla.rows.map(r => texto(r[i]).length))
    ws.getColumn(i + 1).width = Math.min(60, Math.max(12, maxCelda + 3))
  })

  const buffer = await wb.xlsx.writeBuffer()
  descargarBlob(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${tabla.nombre}.xlsx`
  )
}

/** PDF horizontal con título y tabla (jspdf-autotable). `colores` y `bold` opcionales por fila. */
export function exportarPDF(
  tabla: TablaExport,
  opciones?: {
    subtitulo?: string
    colorFila?: (i: number) => [number, number, number] | null
    boldFila?: (i: number) => boolean
  }
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })

  doc.setFontSize(13)
  doc.text(tabla.titulo, 40, 34)
  if (opciones?.subtitulo) {
    doc.setFontSize(9)
    doc.setTextColor(120)
    doc.text(opciones.subtitulo, 40, 48)
    doc.setTextColor(0)
  }

  autoTable(doc, {
    head: [tabla.headers],
    body: tabla.rows.map(r => r.map(texto)),
    startY: opciones?.subtitulo ? 58 : 46,
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak', lineColor: [200, 200, 200] as [number, number, number], lineWidth: 0.3 },
    headStyles: { fillColor: [14, 105, 160], textColor: 255, fontStyle: 'bold' },
    didParseCell: (data) => {
      if (data.section === 'body') {
        if (opciones?.colorFila) {
          const color = opciones.colorFila(data.row.index)
          if (color) {
            const esBoldRow = opciones.boldFila?.(data.row.index) ?? false
            const esUltimaCol = data.column.index === tabla.headers.length - 1
            if (esBoldRow || esUltimaCol) data.cell.styles.fillColor = color
          }
        }
        if (opciones?.boldFila?.(data.row.index)) {
          data.cell.styles.fontStyle = 'bold'
        }
      }
    }
  })

  doc.save(`${tabla.nombre}.pdf`)
}
